import { useState, useCallback, useContext } from 'react';
import { useMutation } from '@apollo/client';

import { useCurrentUser } from '@/store/currentUser';
import { useUserLearningContentLastParticipationLazyQueryRemote } from '@/store/v2';
import { mutationCompleteObject } from '@/administration/pages/UserDetails/queries';
import { SharedSnackbarContext, SnackbarType } from '@/component/SharedSnackbar/SharedSnackbar';
import { getErrorResponseMessage } from '@/utils/gqlErrors';

type UseCompleteLiveEventOptions = {
  liveEventId: string;
  registration?: string;
};

type UseCompleteLiveEventResult = {
  completeAndRefetch: () => Promise<string | null>;
  loading: boolean;
};

export const useCompleteLiveEvent = ({ liveEventId, registration }: UseCompleteLiveEventOptions): UseCompleteLiveEventResult => {
  const [loading, setLoading] = useState(false);
  const [completeObject] = useMutation(mutationCompleteObject);
  const { user } = useCurrentUser();
  const { openSnackbar } = useContext(SharedSnackbarContext);

  const [fetchUserParticipation] = useUserLearningContentLastParticipationLazyQueryRemote();

  const completeAndRefetch = useCallback(async (): Promise<string | null> => {
    if (!user?.id || !liveEventId) {
      return null;
    }

    setLoading(true);

    try {
      const completionResult = await completeObject({
        variables: {
          objectId: `urn:collegial:${liveEventId}`,
          targetUserId: user.id,
          registration: registration || '',
        },
      });

      if (completionResult.errors) {
        openSnackbar({
          type: SnackbarType.DANGER,
          message: getErrorResponseMessage(completionResult.errors),
        });
        return null;
      }

      try {
        const participationResult = await fetchUserParticipation({
          variables: {
            userId: user.id,
            learningContentId: `urn:collegial:self:learning:${liveEventId}`,
          },
        });

        if (participationResult?.data?.userLearningContentLastParticipation?.completedAt) {
          const completedAt = participationResult.data.userLearningContentLastParticipation.completedAt;
          return typeof completedAt === 'string' ? completedAt : completedAt?.toISOString?.();
        }

        return null;
      } catch (participationError) {
        return null;
      }
    } catch (err) {
      openSnackbar({
        type: SnackbarType.DANGER,
        message: 'Failed to complete the live event. Please try again.',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [user?.id, liveEventId, registration, completeObject, fetchUserParticipation, openSnackbar]);

  return {
    completeAndRefetch,
    loading,
  };
};
